/**
 * ドロップ予測の枠の幾何（FR-011、`04-architecture.md`「ドロップ予測の枠」）。
 *
 * グループの footprint（セル集合）から、**輪郭のすぐ内側に沿う閉曲線**の SVG パスを作る。
 * 経路は footprint の境界辺を内側へ `offset` だけ寄せたもので、この幾何から
 *
 * - 凸角は尖ったまま（塊の外側の角とピース境界の対応を保つ）
 * - 凹角（入り隅）は半径 `offset` の円弧でつなぐ
 * - リング状の footprint では内側の穴の縁も独立した閉曲線として描く
 *
 * が従う。DOM を持たない純関数であり、経路の幾何そのものをユニットテストで検証できる。
 */

import { col, row } from '../core/grid';
import type { CellIndex } from '../core/types';
import { at } from './internal';

/** 境界辺の向き（軸平行の単位ベクトル）。footprint の内部が進行方向の右側に来るように張る。 */
interface Direction {
  readonly dx: number;
  readonly dy: number;
}

/** 向き付き境界辺。座標は格子の頂点番号（`vx ∈ 0..C`、`vy ∈ 0..R`）。 */
interface BoundaryEdge {
  readonly fromX: number;
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
  readonly dir: Direction;
}

/**
 * footprint の境界を辿る SVG パス（`d` 属性値）を返す。
 *
 * 外周と穴の縁がそれぞれ独立した閉曲線（`M … Z`）になり、1 つの `d` に連結される。
 * 1 グループ = 1 つの `<path>` 要素に対応させるためである。
 *
 * @param cells footprint のセル集合（重複なし）
 * @param C 列数
 * @param w ピースの論理幅
 * @param h ピースの論理高さ
 * @param offset 内側へのオフセット量（= 凹角の円弧半径、論理 px）
 */
export function outlinePath(
  cells: readonly CellIndex[],
  C: number,
  w: number,
  h: number,
  offset: number,
): string {
  return boundaryLoops(cells, C)
    .map((loop) => loopPath(loop, w, h, offset))
    .join(' ');
}

/**
 * footprint の向き付き境界辺を閉路へ束ねる。
 *
 * 内部を進行方向の右側に置く向きで各辺を張るため、外周は時計回り（画面座標）、
 * 穴の縁は反時計回りの閉路になる。**辺の後続は使用状況によらない決定的な対応**であり
 * （通常の頂点では唯一の続き、対角でくびれる頂点では左折）、この対応は全境界辺の置換に
 * なる。したがって任意の辺から後続を辿れば必ず出発の辺に戻り、各辺はちょうど 1 つの
 * 閉路に属する。
 *
 * くびれの頂点で左折を選ぶことで、外周と穴の縁がそこで融合せず、両側の凹角の円弧が
 * 頂点から互いに離れる向きに丸まる（経路が自分自身と交差しない。`04-architecture.md`）。
 */
function boundaryLoops(cells: readonly CellIndex[], C: number): BoundaryEdge[][] {
  const inSet = new Set<CellIndex>(cells);
  const outgoing = new Map<string, BoundaryEdge[]>();
  const edges: BoundaryEdge[] = [];
  const add = (fromX: number, fromY: number, toX: number, toY: number): void => {
    const edge: BoundaryEdge = {
      fromX,
      fromY,
      toX,
      toY,
      dir: { dx: Math.sign(toX - fromX), dy: Math.sign(toY - fromY) },
    };
    edges.push(edge);
    const key = `${fromX},${fromY}`;
    const list = outgoing.get(key);
    if (list) list.push(edge);
    else outgoing.set(key, [edge]);
  };

  for (const cell of cells) {
    const cx = col(cell, C);
    const cy = row(cell, C);
    // 各辺は「その方向の隣が footprint に無い」ときだけ境界になる。盤の端の判定を
    // 添字の剰余に任せない（右端の cell + 1 は次の行の先頭を指してしまう）。
    if (!inSet.has(cell - C)) add(cx, cy, cx + 1, cy); // 上辺: 左 -> 右
    if (cx === C - 1 || !inSet.has(cell + 1)) add(cx + 1, cy, cx + 1, cy + 1); // 右辺: 上 -> 下
    if (!inSet.has(cell + C)) add(cx + 1, cy + 1, cx, cy + 1); // 下辺: 右 -> 左
    if (cx === 0 || !inSet.has(cell - 1)) add(cx, cy + 1, cx, cy); // 左辺: 下 -> 上
  }

  const loops: BoundaryEdge[][] = [];
  const used = new Set<BoundaryEdge>();
  for (const seed of edges) {
    if (used.has(seed)) continue;
    const loop: BoundaryEdge[] = [];
    let current = seed;
    do {
      used.add(current);
      loop.push(current);
      current = successor(outgoing, current);
    } while (current !== seed);
    loops.push(mergeCollinear(loop));
  }
  return loops;
}

/** `current` の終点から出る後続の辺。くびれの頂点（候補 2 本）では左折を採る。 */
function successor(
  outgoing: ReadonlyMap<string, BoundaryEdge[]>,
  current: BoundaryEdge,
): BoundaryEdge {
  const candidates = outgoing.get(`${current.toX},${current.toY}`);
  // 境界辺の入次数と出次数は各頂点で釣り合うため、必ず続きがある。
  /* v8 ignore next */
  if (!candidates || candidates.length === 0)
    throw new Error(`内部エラー: 頂点 (${current.toX}, ${current.toY}) で境界が途切れました。`);
  if (candidates.length === 1) return at(candidates, 0);
  const left = candidates.find((edge) => cross(current.dir, edge.dir) < 0);
  /* v8 ignore next */
  if (!left) throw new Error('内部エラー: くびれの頂点に左折候補がありません。');
  return left;
}

/** 同方向に連続する辺を 1 本にまとめる（閉路の先頭と末尾のまたぎも含む）。 */
function mergeCollinear(loop: readonly BoundaryEdge[]): BoundaryEdge[] {
  const merged: BoundaryEdge[] = [];
  for (const edge of loop) {
    const last = merged[merged.length - 1];
    if (last && last.dir.dx === edge.dir.dx && last.dir.dy === edge.dir.dy) {
      merged[merged.length - 1] = { ...last, toX: edge.toX, toY: edge.toY };
    } else {
      merged.push(edge);
    }
  }
  const first = at(merged, 0);
  const last = at(merged, merged.length - 1);
  if (merged.length > 1 && first.dir.dx === last.dir.dx && first.dir.dy === last.dir.dy) {
    merged[0] = { ...first, fromX: last.fromX, fromY: last.fromY };
    merged.pop();
  }
  return merged;
}

/**
 * 1 つの閉路を、内側へ `offset` 寄せた SVG パスに変換する。
 *
 * 各頂点（辺と辺の継ぎ目）で、右折（凸角）は 2 辺のオフセット線の交点 1 点、
 * 左折（凹角）は元の頂点を中心とする半径 `offset` の四分円でつなぐ。
 */
function loopPath(loop: readonly BoundaryEdge[], w: number, h: number, offset: number): string {
  const parts: string[] = [];
  let lastPoint = '';
  for (const [i, edge] of loop.entries()) {
    const next = at(loop, (i + 1) % loop.length);
    const vx = edge.toX * w;
    const vy = edge.toY * h;
    const nA = inwardNormal(edge.dir, offset);
    const nB = inwardNormal(next.dir, offset);
    if (cross(edge.dir, next.dir) > 0) {
      // 右折 = 凸角。オフセット線の交点で尖ったままつなぐ。
      lastPoint = `${fmt(vx + nA.x + nB.x)} ${fmt(vy + nA.y + nB.y)}`;
      parts.push(`L ${lastPoint}`);
    } else {
      // 左折 = 凹角。半径 offset の円弧（中心は元の頂点）。画面座標で反時計回り（sweep 0）。
      lastPoint = `${fmt(vx + nB.x)} ${fmt(vy + nB.y)}`;
      parts.push(
        `L ${fmt(vx + nA.x)} ${fmt(vy + nA.y)}`,
        `A ${fmt(offset)} ${fmt(offset)} 0 0 0 ${lastPoint}`,
      );
    }
  }
  // 閉路の最後の頂点の到達点が、そのまま最初の辺の始点になる。
  return `M ${lastPoint} ${parts.join(' ')} Z`;
}

/** 進行方向の右側（= 内部側）を向く長さ `offset` の法線。画面座標（y は下向き）。 */
function inwardNormal(dir: Direction, offset: number): { x: number; y: number } {
  return { x: -dir.dy * offset, y: dir.dx * offset };
}

/** 外積の z 成分。画面座標では正が右折（時計回り）、負が左折。 */
function cross(a: Direction, b: Direction): number {
  return a.dx * b.dy - a.dy * b.dx;
}

/** 数値の整形。誤差の出ない整数格子 × 整数オフセットを前提に、そのまま文字列化する。 */
function fmt(n: number): string {
  return String(n);
}
